/**
 * File Validation Utilities
 *
 * Validators for CSV, JSON, XML, XLSX, and ZIP file formats.
 * Each validator parses the file, extracts preview data, and returns metadata.
 *
 * @module components/import/utils
 */

import JSZip from 'jszip';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';

import { ValidationResult } from '@/components/import/types';

/**
 * CSV file validator
 *
 * Parses CSV files using papaparse to properly handle quoted fields,
 * newlines within fields, and various CSV formats.
 *
 * @param file - CSV file to validate
 * @param previewRows - Number of rows to preview (default: 10)
 * @returns Validation result with preview data
 */
export async function validateCSV(
  file: File,
  previewRows = 10
): Promise<ValidationResult> {
  try {
    const text = await file.text();

    // Parse CSV using papaparse
    const result = Papa.parse<Record<string, string>>(text, {
      header: true,
      skipEmptyLines: true,
      dynamicTyping: false, // Keep all values as strings for preview
    }) as Papa.ParseResult<Record<string, string>>;

    if (result.errors && result.errors.length > 0) {
      // Check for critical errors
      const criticalErrors = result.errors.filter(
        (err: Papa.ParseError) =>
          err.type === 'Quotes' || err.type === 'FieldMismatch'
      );
      if (criticalErrors.length > 0) {
        return {
          valid: false,
          error: `CSV parsing error: ${criticalErrors[0].message}`,
        };
      }
    }

    if (!result.data || result.data.length === 0) {
      return {
        valid: false,
        error: 'File is empty or contains no data rows',
      };
    }

    // Get headers from the first data row
    const headers = Object.keys(result.data[0]);

    if (headers.length === 0) {
      return {
        valid: false,
        error: 'No headers found in CSV',
      };
    }

    // Get preview rows
    const previewData = result.data.slice(0, previewRows);

    return {
      valid: true,
      preview: {
        headers,
        rows: previewData,
        totalRows: result.data.length,
      },
      metadata: {
        fileName: file.name,
        fileSize: file.size,
        rowCount: result.data.length,
        columnCount: headers.length,
      },
    };
  } catch (error) {
    return {
      valid: false,
      error: `Failed to parse CSV: ${error instanceof Error ? error.message : 'Unknown error'}`,
    };
  }
}

/**
 * JSON file validator
 *
 * Parses JSON files and validates they contain an array of objects.
 * Extracts headers from first object and creates preview.
 *
 * @param file - JSON file to validate
 * @param previewRows - Number of rows to preview (default: 10)
 * @returns Validation result with preview data
 */
export async function validateJSON(
  file: File,
  previewRows = 10
): Promise<ValidationResult> {
  try {
    const text = await file.text();
    const data = JSON.parse(text);

    if (!Array.isArray(data)) {
      return {
        valid: false,
        error: 'JSON must be an array of objects',
      };
    }

    if (data.length === 0) {
      return {
        valid: false,
        error: 'JSON array is empty',
      };
    }

    // Get headers from first object
    const firstItem = data[0];
    if (typeof firstItem !== 'object' || firstItem === null) {
      return {
        valid: false,
        error: 'JSON array must contain objects',
      };
    }

    const headers = Object.keys(firstItem);

    return {
      valid: true,
      preview: {
        headers,
        rows: data.slice(0, previewRows),
        totalRows: data.length,
      },
      metadata: {
        fileName: file.name,
        fileSize: file.size,
        rowCount: data.length,
        columnCount: headers.length,
      },
    };
  } catch (error) {
    return {
      valid: false,
      error: `Failed to parse JSON: ${error instanceof Error ? error.message : 'Unknown error'}`,
    };
  }
}

/**
 * XML file validator
 *
 * Validates XML files using DOMParser.
 * For preview, shows first N lines of XML as text (doesn't parse structure).
 *
 * @param file - XML file to validate
 * @param previewRows - Number of lines to preview (default: 10)
 * @returns Validation result with text preview
 */
export async function validateXML(
  file: File,
  previewRows = 10
): Promise<ValidationResult> {
  try {
    const text = await file.text();
    const parser = new DOMParser();
    const doc = parser.parseFromString(text, 'text/xml');

    // Check for parse errors
    const parseError = doc.querySelector('parsererror');
    if (parseError) {
      return {
        valid: false,
        error: 'Invalid XML format',
      };
    }

    // Display first N lines as text preview
    const lines = text.split('\n').slice(0, previewRows);

    return {
      valid: true,
      preview: {
        rows: lines.map(line => ({ xml: line })),
        totalRows: text.split('\n').length,
      },
      metadata: {
        fileName: file.name,
        fileSize: file.size,
      },
    };
  } catch (error) {
    return {
      valid: false,
      error: `Failed to parse XML: ${error instanceof Error ? error.message : 'Unknown error'}`,
    };
  }
}

/**
 * XLSX file validator
 *
 * Parses XLSX (Excel) files using the xlsx library.
 * Extracts headers from first row and creates preview of data.
 *
 * @param file - XLSX file to validate
 * @param previewRows - Number of rows to preview (default: 10)
 * @returns Validation result with preview data
 */
export async function validateXLSX(
  file: File,
  previewRows = 10
): Promise<ValidationResult> {
  try {
    // Read file as ArrayBuffer
    const arrayBuffer = await file.arrayBuffer();

    // Parse workbook
    const workbook = XLSX.read(arrayBuffer, { type: 'array' });

    // Validate workbook has sheets
    if (!workbook.SheetNames || workbook.SheetNames.length === 0) {
      return {
        valid: false,
        error: 'No sheets found in Excel file',
      };
    }

    // Get first sheet
    const firstSheetName = workbook.SheetNames[0];
    const worksheet = workbook.Sheets[firstSheetName];

    // Convert to JSON (array of objects)
    const data = XLSX.utils.sheet_to_json<Record<string, string>>(worksheet, {
      raw: false, // Convert all values to strings for preview
      defval: '', // Default value for empty cells
    });

    if (data.length === 0) {
      return {
        valid: false,
        error: 'Sheet is empty or contains no data rows',
      };
    }

    // Get headers from first row
    const headers = Object.keys(data[0]);

    if (headers.length === 0) {
      return {
        valid: false,
        error: 'No headers found in Excel sheet',
      };
    }

    // Get preview rows
    const previewData = data.slice(0, previewRows);

    return {
      valid: true,
      preview: {
        headers,
        rows: previewData,
        totalRows: data.length,
      },
      metadata: {
        fileName: file.name,
        fileSize: file.size,
        rowCount: data.length,
        columnCount: headers.length,
      },
    };
  } catch (error) {
    return {
      valid: false,
      error: `Failed to parse XLSX: ${error instanceof Error ? error.message : 'Unknown error'}`,
    };
  }
}

/**
 * ZIP file validator
 *
 * Validates ZIP archive files and lists contents without extracting.
 * Displays first N filenames from the archive as preview.
 *
 * @param file - ZIP file to validate
 * @param previewRows - Number of files to preview (default: 10)
 * @returns Validation result with file list preview
 */
export async function validateZIP(
  file: File,
  previewRows = 10
): Promise<ValidationResult> {
  try {
    // Read file as ArrayBuffer
    const arrayBuffer = await file.arrayBuffer();

    // Load ZIP archive
    const zip = await JSZip.loadAsync(arrayBuffer);

    // Get list of files in the archive (excluding directories)
    const files = Object.keys(zip.files).filter(
      filename => !zip.files[filename].dir
    );

    if (files.length === 0) {
      return {
        valid: false,
        error: 'ZIP archive is empty or contains no files',
      };
    }

    // Get preview of first N filenames
    const previewFiles = files.slice(0, previewRows);

    return {
      valid: true,
      preview: {
        rows: previewFiles.map(filename => ({ filename })),
        totalRows: files.length,
      },
      metadata: {
        fileName: file.name,
        fileSize: file.size,
        rowCount: files.length, // Total number of files in archive
      },
    };
  } catch (error) {
    return {
      valid: false,
      error: `Failed to parse ZIP: ${error instanceof Error ? error.message : 'Unknown error'}`,
    };
  }
}

/**
 * TXT file validator
 *
 * Validates plain text files and displays first N lines as preview.
 * Used for seed import files where each line represents a seed value.
 *
 * @param file - TXT file to validate
 * @param previewRows - Number of lines to preview (default: 10)
 * @returns Validation result with text preview
 */
export async function validateTXT(
  file: File,
  previewRows = 10
): Promise<ValidationResult> {
  try {
    const text = await file.text();

    if (!text || text.trim().length === 0) {
      return {
        valid: false,
        error: 'File is empty',
      };
    }

    // Split into lines and filter out empty lines
    const allLines = text.split('\n');
    const nonEmptyLines = allLines.filter(line => line.trim().length > 0);

    if (nonEmptyLines.length === 0) {
      return {
        valid: false,
        error: 'File contains no non-empty lines',
      };
    }

    // Get preview of first N lines
    const previewLines = allLines.slice(0, previewRows);

    return {
      valid: true,
      preview: {
        rows: previewLines.map(line => ({ line })),
        totalRows: allLines.length,
      },
      metadata: {
        fileName: file.name,
        fileSize: file.size,
        rowCount: nonEmptyLines.length, // Count only non-empty lines
      },
    };
  } catch (error) {
    return {
      valid: false,
      error: `Failed to parse TXT: ${error instanceof Error ? error.message : 'Unknown error'}`,
    };
  }
}

/**
 * Seed file validator
 *
 * Validates seed import files which can be XLSX, CSV, or TXT format.
 * Routes to appropriate validator based on file extension.
 *
 * @param file - Seed file to validate (XLSX, CSV, or TXT)
 * @param previewRows - Number of rows/lines to preview (default: 10)
 * @returns Validation result with preview data
 */
export async function validateSeedFile(
  file: File,
  previewRows = 10
): Promise<ValidationResult> {
  const fileName = file.name.toLowerCase();

  if (fileName.endsWith('.xlsx') || fileName.endsWith('.xls')) {
    return validateXLSX(file, previewRows);
  } else if (fileName.endsWith('.csv')) {
    return validateCSV(file, previewRows);
  } else if (fileName.endsWith('.txt')) {
    return validateTXT(file, previewRows);
  } else {
    return {
      valid: false,
      error: 'File must be .xlsx, .csv, or .txt format',
    };
  }
}
